import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { FileCheck, Home, DollarSign, Users, Clock, MapPin, ArrowRight } from "lucide-react";

const Services = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 mt-36">
              Our Services
            </h1>
            <p className="text-xl lg:text-2xl max-w-3xl mx-auto text-white/90">
              Comprehensive end-to-end support for your Brazilian residency journey
            </p>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Complete Residency Solutions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From initial consultation to post-arrival support, we handle every aspect of your Brazilian residency process
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="shadow-corporate hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Home className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-3">VIPER Residency Program</h3>
                <p className="text-gray-600 mb-4">
                  Complete guidance through Brazil's Investor Residency (VIPER) program, ensuring compliance with all requirements and regulations.
                </p>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>• Program eligibility assessment</li>
                  <li>• Investment requirement guidance</li>
                  <li>• Application preparation and filing</li>
                  <li>• Follow-up and status monitoring</li>
                </ul>
                <Link to="/residency-process" className="text-primary hover:text-primary/80 font-medium inline-flex items-center">
                  Learn more <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </CardContent>
            </Card>
            
            <Card className="shadow-corporate hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <MapPin className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-3">Property Sourcing & Due Diligence</h3>
                <p className="text-gray-600 mb-4">
                  Professional property evaluation and architectural due diligence services by our Brazilian architect.
                </p>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>• Market analysis and property identification</li>
                  <li>• Technical property evaluation</li>
                  <li>• Legal title verification</li>
                  <li>• Investment viability assessment</li>
                </ul>
                <Link to="/contact" className="text-primary hover:text-primary/80 font-medium inline-flex items-center">
                  Get started <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </CardContent>
            </Card>
            
            <Card className="shadow-corporate hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <FileCheck className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-3">Legal Documentation Management</h3>
                <p className="text-gray-600 mb-4">
                  Complete legal and immigration documentation management handled by our Nigerian lawyer specialist.
                </p>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>• Document preparation and review</li>
                  <li>• Apostille and authentication services</li>
                  <li>• Translation and certification</li>
                  <li>• Compliance verification</li>
                </ul>
                <Link to="/contact" className="text-primary hover:text-primary/80 font-medium inline-flex items-center">
                  Get started <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </CardContent>
            </Card>
            
            <Card className="shadow-corporate hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <DollarSign className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-3">Capital Transfer Guidance</h3>
                <p className="text-gray-600 mb-4">
                  Expert guidance on legal capital transfer methods and compliance with both Nigerian and Brazilian regulations.
                </p>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>• Transfer method consultation</li>
                  <li>• Regulatory compliance guidance</li>
                  <li>• Banking relationship facilitation</li>
                  <li>• Documentation support</li>
                </ul>
                <Link to="/contact" className="text-primary hover:text-primary/80 font-medium inline-flex items-center">
                  Get started <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </CardContent>
            </Card>
            
            <Card className="shadow-corporate hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-3">Application Filing & Monitoring</h3>
                <p className="text-gray-600 mb-4">
                  Professional application filing, follow-up, renewals, and long-term residency status monitoring.
                </p>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>• Application submission</li>
                  <li>• Status tracking and updates</li>
                  <li>• Renewal management</li>
                  <li>• Long-term compliance monitoring</li>
                </ul>
                <Link to="/contact" className="text-primary hover:text-primary/80 font-medium inline-flex items-center">
                  Get started <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </CardContent>
            </Card>
            
            <Card className="shadow-corporate hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-3">Post-Arrival Onboarding</h3>
                <p className="text-gray-600 mb-4">
                  Comprehensive support for your transition to life in Brazil, ensuring a smooth settlement process.
                </p>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>• Local registration assistance</li>
                  <li>• Banking and financial setup</li>
                  <li>• Healthcare system navigation</li>
                  <li>• Cultural orientation support</li>
                </ul>
                <Link to="/contact" className="text-primary hover:text-primary/80 font-medium inline-flex items-center">
                  Get started <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Service Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A structured, step-by-step approach ensuring transparency and success at every stage
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">
                1
              </div>
              <h3 className="font-semibold text-lg mb-2">Initial Consultation</h3>
              <p className="text-gray-600 text-sm">
                Comprehensive assessment of your requirements and eligibility
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">
                2
              </div>
              <h3 className="font-semibold text-lg mb-2">Strategy Development</h3>
              <p className="text-gray-600 text-sm">
                Customized residency strategy and investment plan creation
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">
                3
              </div>
              <h3 className="font-semibold text-lg mb-2">Implementation</h3>
              <p className="text-gray-600 text-sm">
                Property sourcing, documentation, and application filing
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">
                4
              </div>
              <h3 className="font-semibold text-lg mb-2">Ongoing Support</h3>
              <p className="text-gray-600 text-sm">
                Continuous monitoring and post-arrival assistance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 corporate-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Begin Your Brazilian Residency Journey?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Contact our expert team to discuss your specific requirements and learn how we can support your residency goals.
          </p>
          <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90">
            <Link to="/contact">Schedule Your Consultation</Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;